/**
 * View Reset Utilities
 * 
 * Pure computation functions for view reset and animation calculations.
 * Extracted from useFocusViewReset for maintainability.
 */

/**
 * Easing function: ease out cubic
 * @param {number} t - Progress from 0 to 1
 * @returns {number} Eased progress
 */
export const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Default node dimensions when not specified
 */
export const DEFAULT_NODE_DIMS = { width: 290, height: 150 };

/**
 * Calculate bounding box for a set of nodes
 * 
 * @param {Set|Array} nodeIds - IDs of nodes to include
 * @param {Object} positions - Position map { taskId: { x, y } }
 * @param {Object} dimensions - Dimensions map { taskId: { width, height } }
 * @returns {{ minX, minY, maxX, maxY, width, height, centerX, centerY } | null}
 */
export function calculateBoundingBox(nodeIds, positions, dimensions) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  const ids = nodeIds instanceof Set ? nodeIds : new Set(nodeIds);
  
  ids.forEach(taskId => {
    const pos = positions[taskId];
    if (!pos) return;
    
    const dims = dimensions[taskId] || DEFAULT_NODE_DIMS;
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x + dims.width);
    maxY = Math.max(maxY, pos.y + dims.height);
  });
  
  if (minX === Infinity) return null;
  
  const width = maxX - minX;
  const height = maxY - minY;
  
  return {
    minX,
    minY,
    maxX,
    maxY,
    width,
    height,
    centerX: minX + width / 2,
    centerY: minY + height / 2,
  };
}

/**
 * Calculate pan position to center content in viewport
 * 
 * @param {Object} params
 * @param {number} params.contentCenterX - X center of content
 * @param {number} params.contentCenterY - Y center of content
 * @param {number} params.viewportWidth - Width of viewport
 * @param {number} params.viewportHeight - Height of viewport
 * @param {number} params.zoom - Current zoom level
 * @returns {{ x: number, y: number }}
 */
export function calculateCenteredPan({ contentCenterX, contentCenterY, viewportWidth, viewportHeight, zoom }) {
  return {
    x: viewportWidth / 2 - contentCenterX * zoom,
    y: viewportHeight / 2 - contentCenterY * zoom,
  };
}

/**
 * Calculate zoom level to fit content in viewport
 * 
 * @param {Object} params
 * @param {number} params.contentWidth - Width of content
 * @param {number} params.contentHeight - Height of content
 * @param {number} params.viewportWidth - Available viewport width
 * @param {number} params.viewportHeight - Available viewport height
 * @param {number} [params.maxZoom=1.0] - Maximum zoom level
 * @param {number} [params.minZoom=0.3] - Minimum zoom level
 * @param {number} [params.marginFactor=0.9] - Factor to leave margin (0.9 = 10% margin)
 * @returns {number} Calculated zoom level
 */
export function calculateFitZoom({
  contentWidth,
  contentHeight,
  viewportWidth,
  viewportHeight,
  maxZoom = 1.0,
  minZoom = 0.3,
  marginFactor = 0.9,
}) {
  const scaleX = viewportWidth / contentWidth;
  const scaleY = viewportHeight / contentHeight;
  const fitZoom = Math.min(scaleX, scaleY, maxZoom) * marginFactor;
  return Math.max(fitZoom, minZoom);
}

/**
 * Check if content fits in viewport at current zoom
 * 
 * @param {Object} params
 * @param {number} params.contentWidth - Width of content
 * @param {number} params.contentHeight - Height of content
 * @param {number} params.viewportWidth - Available viewport width
 * @param {number} params.viewportHeight - Available viewport height
 * @param {number} params.zoom - Current zoom level
 * @param {number} [params.buffer=0.95] - Buffer factor (0.95 = 5% buffer)
 * @returns {boolean} True if content fits
 */
export function contentFitsInViewport({
  contentWidth,
  contentHeight,
  viewportWidth,
  viewportHeight,
  zoom,
  buffer = 0.95,
}) {
  return contentWidth * zoom <= viewportWidth * buffer &&
         contentHeight * zoom <= viewportHeight * buffer;
}

/**
 * Calculate target zoom for centering on a task
 * Adjusts zoom if too far out (< 0.7) or too far in (> 1.3)
 * 
 * @param {number} currentZoom - Current zoom level
 * @param {boolean} adjustZoom - Whether to adjust zoom
 * @returns {number} Target zoom level
 */
export function calculateCenterTaskZoom(currentZoom, adjustZoom = true) {
  if (!adjustZoom) return currentZoom;
  if (currentZoom < 0.7 || currentZoom > 1.3) {
    return 1.0;
  }
  return currentZoom;
}

/**
 * Calculate instant pan position for focus mode
 * Handles different cases based on whether content fits in viewport
 * 
 * @param {Object} params
 * @param {Object} params.boundingBox - Bounding box from calculateBoundingBox
 * @param {number} params.viewportWidth - Container width
 * @param {number} params.viewportHeight - Container height
 * @param {number} params.padding - Padding around content
 * @returns {{ x: number, y: number }}
 */
export function calculateInstantFocusPan({ boundingBox, viewportWidth, viewportHeight, padding }) {
  const { minX, minY, width, height, centerX, centerY } = boundingBox;
  
  const availableWidth = viewportWidth - padding * 2;
  const availableHeight = viewportHeight - padding * 2;
  
  const fitsWidth = width <= availableWidth;
  const fitsHeight = height <= availableHeight;
  
  let targetPanX, targetPanY;
  
  if (fitsWidth && fitsHeight) {
    // Center both axes
    targetPanX = viewportWidth / 2 - centerX;
    targetPanY = viewportHeight / 2 - centerY;
  } else if (fitsHeight && !fitsWidth) {
    // Align left, center vertically
    targetPanX = padding - minX;
    targetPanY = viewportHeight / 2 - centerY;
  } else if (fitsWidth && !fitsHeight) {
    // Center horizontally, align top
    targetPanX = viewportWidth / 2 - centerX;
    targetPanY = padding - minY;
  } else {
    // Align top-left
    targetPanX = padding - minX;
    targetPanY = padding - minY;
  }
  
  return { x: targetPanX, y: targetPanY };
}

/**
 * Calculate "start" position for focus mode - same logic as toolbar "Start" button
 * This positions content like handleGoToStart does:
 * - If content fits horizontally: center it
 * - If content doesn't fit: show top-left with padding (like reading left-to-right)
 * - Same for vertical axis
 * 
 * @param {Object} params
 * @param {Object} params.boundingBox - Bounding box from calculateBoundingBox
 * @param {number} params.viewportWidth - Container width
 * @param {number} params.viewportHeight - Container height
 * @param {number} params.zoom - Target zoom level (usually 1.0)
 * @returns {{ x: number, y: number }}
 */
export function calculateStartPositionPan({ boundingBox, viewportWidth, viewportHeight, zoom }) {
  const { minX, minY, width, height, centerX, centerY } = boundingBox;
  
  const contentWidth = width * zoom;
  const contentHeight = height * zoom;
  
  // Padding for when we align to edges (same as handleGoToStart)
  const edgePaddingX = Math.min(viewportWidth * 0.1, 60);
  const edgePaddingY = Math.min(viewportHeight * 0.1, 60);
  
  // Check if content fits in viewport
  const fitsHorizontally = contentWidth + (edgePaddingX * 2) <= viewportWidth;
  const fitsVertically = contentHeight + (edgePaddingY * 2) <= viewportHeight;
  
  let targetPanX, targetPanY;
  
  if (fitsHorizontally) {
    // Center horizontally
    targetPanX = (viewportWidth / 2) - (centerX * zoom);
  } else {
    // Align left with padding (like reading left-to-right)
    const leftPadding = Math.min(viewportWidth * 0.3, 200);
    targetPanX = leftPadding - minX * zoom;
  }
  
  if (fitsVertically) {
    // Center vertically
    targetPanY = (viewportHeight / 2) - (centerY * zoom);
  } else {
    // Align top with padding
    const topPadding = Math.min(viewportHeight * 0.2, 100);
    targetPanY = topPadding - minY * zoom;
  }
  
  return { x: targetPanX, y: targetPanY };
}

/**
 * Check if animation can be skipped (already at target)
 * 
 * @param {Object} params
 * @param {number} params.deltaX - Pan X delta
 * @param {number} params.deltaY - Pan Y delta
 * @param {number} params.deltaZoom - Zoom delta
 * @param {number} [params.panThreshold=1] - Threshold for pan delta
 * @param {number} [params.zoomThreshold=0.01] - Threshold for zoom delta
 * @returns {boolean} True if animation can be skipped
 */
export function canSkipAnimation({ deltaX, deltaY, deltaZoom, panThreshold = 1, zoomThreshold = 0.01 }) {
  return Math.abs(deltaX) < panThreshold && 
         Math.abs(deltaY) < panThreshold && 
         Math.abs(deltaZoom) < zoomThreshold;
}

/**
 * Interpolate between two values with easing
 * 
 * @param {number} start - Start value
 * @param {number} end - End value
 * @param {number} progress - Progress from 0 to 1
 * @param {Function} [easingFn=easeOutCubic] - Easing function
 * @returns {number} Interpolated value
 */
export function interpolate(start, end, progress, easingFn = easeOutCubic) {
  const easedProgress = easingFn(progress);
  return start + (end - start) * easedProgress;
}

/**
 * Interpolate pan position
 * 
 * @param {Object} startPan - Start pan { x, y }
 * @param {Object} targetPan - Target pan { x, y }
 * @param {number} progress - Progress from 0 to 1
 * @param {Function} [easingFn=easeOutCubic] - Easing function
 * @returns {{ x: number, y: number }}
 */
export function interpolatePan(startPan, targetPan, progress, easingFn = easeOutCubic) {
  const easedProgress = easingFn(progress);
  return {
    x: startPan.x + (targetPan.x - startPan.x) * easedProgress,
    y: startPan.y + (targetPan.y - startPan.y) * easedProgress,
  };
}

/**
 * Check if a node is fully visible in the viewport
 * 
 * @param {Object} nodePos - Node position { x, y }
 * @param {Object} nodeDims - Node dimensions { width, height }
 * @param {Object} pan - Current pan { x, y }
 * @param {number} zoom - Current zoom level
 * @param {number} viewportWidth - Viewport width
 * @param {number} viewportHeight - Viewport height
 * @returns {boolean} True if node is fully visible
 */
export function isNodeFullyVisible(nodePos, nodeDims, pan, zoom, viewportWidth, viewportHeight) {
  // Convert node bounds to screen coordinates
  const nodeLeft = nodePos.x * zoom + pan.x;
  const nodeTop = nodePos.y * zoom + pan.y;
  const nodeRight = nodeLeft + nodeDims.width * zoom;
  const nodeBottom = nodeTop + nodeDims.height * zoom;
  
  // Check if entire node is within viewport
  return nodeLeft >= 0 && 
         nodeTop >= 0 && 
         nodeRight <= viewportWidth && 
         nodeBottom <= viewportHeight;
}

/**
 * Find the nearest node to the viewport center that can be panned to
 * 
 * @param {Array} tasks - Array of tasks
 * @param {Object} positions - Position map { taskId: { x, y } }
 * @param {Object} dimensions - Dimensions map { taskId: { width, height } }
 * @param {Object} pan - Current pan { x, y }
 * @param {number} zoom - Current zoom level
 * @param {number} viewportWidth - Viewport width
 * @param {number} viewportHeight - Viewport height
 * @returns {{ taskId: string, position: { x, y }, dimensions: { width, height } } | null}
 */
export function findNearestNodeToViewport(tasks, positions, dimensions, pan, zoom, viewportWidth, viewportHeight) {
  if (!tasks || tasks.length === 0) return null;
  
  // Calculate viewport center in canvas coordinates
  const viewportCenterX = (viewportWidth / 2 - pan.x) / zoom;
  const viewportCenterY = (viewportHeight / 2 - pan.y) / zoom;
  
  let nearestTask = null;
  let nearestDistance = Infinity;
  
  tasks.forEach(task => {
    const pos = positions[task.id];
    if (!pos) return;
    
    const dims = dimensions[task.id] || DEFAULT_NODE_DIMS;
    
    // Calculate node center
    const nodeCenterX = pos.x + dims.width / 2;
    const nodeCenterY = pos.y + dims.height / 2;
    
    // Calculate distance from viewport center to node center
    const distance = Math.sqrt(
      Math.pow(nodeCenterX - viewportCenterX, 2) + 
      Math.pow(nodeCenterY - viewportCenterY, 2)
    );
    
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestTask = { taskId: task.id, position: pos, dimensions: dims };
    }
  });
  
  return nearestTask;
}

/**
 * Check if any node is fully visible in the viewport
 * 
 * @param {Array} tasks - Array of tasks
 * @param {Object} positions - Position map { taskId: { x, y } }
 * @param {Object} dimensions - Dimensions map { taskId: { width, height } }
 * @param {Object} pan - Current pan { x, y }
 * @param {number} zoom - Current zoom level
 * @param {number} viewportWidth - Viewport width
 * @param {number} viewportHeight - Viewport height
 * @returns {boolean} True if at least one node is fully visible
 */
export function hasAnyFullyVisibleNode(tasks, positions, dimensions, pan, zoom, viewportWidth, viewportHeight) {
  if (!tasks || tasks.length === 0) return false;
  
  return tasks.some(task => {
    const pos = positions[task.id];
    if (!pos) return false;
    
    const dims = dimensions[task.id] || DEFAULT_NODE_DIMS;
    return isNodeFullyVisible(pos, dims, pan, zoom, viewportWidth, viewportHeight);
  });
}